import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Service } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { EnvironmentLoaderService } from './environment-loader.service';
import {
  CreateDepartmentRequest,
  Department,
  DepartmentSearchEvent,
  UpdateDepartmentRequest,
} from 'src/app/features/department/department.model';
import { PagedResult } from '../models/paging';
import { LocalStorageDataService } from './local-storage.service';
import { PageEvent } from '@angular/material/paginator';

@Service()
export class DepartmentService {
  private http = inject(HttpClient);
  private environmentLoaderService = inject(EnvironmentLoaderService);
  private localStorageService = inject(LocalStorageDataService);

  private readonly departmentStorageKey = 'departments';
  private readonly departmentStorageTimestampKey = 'departmentStorageTimestamp';
  private readonly dataCheckInterval = 12 * 60 * 60 * 1000; // 12 hours in milliseconds

  apiUrl: string;

  constructor() {
    this.apiUrl = this.environmentLoaderService.config['serviceBaseUrl'] + '/api/v1/admin/Department';
  }

  public getDepartments(searchEvent?: DepartmentSearchEvent): Observable<PagedResult<Department>> {
    const url = `${this.apiUrl}`;
    let params = new HttpParams();

    if (searchEvent?.organizationId) {
      params = params.append('organizationId', searchEvent.organizationId);
    }
    if (searchEvent?.query) {
      params = params.append('query', searchEvent.query);
    }
    if (searchEvent?.paging?.pageIndex) {
      params = params.append('page', searchEvent?.paging?.pageIndex);
    }
    if (searchEvent?.paging?.pageSize) {
      params = params.append('pageSize', searchEvent?.paging?.pageSize);
    }

    return this.http.get<PagedResult<Department>>(url, {
      params,
    });
  }

  public getDepartment(id: number): Observable<Department> {
    const url = `${this.apiUrl}/${id}`;

    return this.http.get<Department>(url);
  }

  public createDepartment(organizationId: number, request: CreateDepartmentRequest): Observable<number> {
    const url = `${this.apiUrl}?organizationId=${organizationId}`;
    return this.http.post<number>(url, request);
  }

  public updateDepartment(departmentId: number, request: UpdateDepartmentRequest): Observable<void> {
    const url = `${this.apiUrl}/${departmentId}`;
    return this.http.put<void>(url, request);
  }

  public deleteDepartment(departmentId: number): Observable<void> {
    const url = `${this.apiUrl}/${departmentId}`;
    return this.http.delete<void>(url);
  }

  public async getAndStoreDepartments(): Promise<Department[]> {
    const departmentStorageTimestamp: string = this.localStorageService.get(this.departmentStorageTimestampKey);
    let refreshDepartmentsInStorage = true;
    if (departmentStorageTimestamp) {
      refreshDepartmentsInStorage = new Date(departmentStorageTimestamp).getTime() + this.dataCheckInterval < Date.now();
    }

    let departments: Department[] = this.localStorageService.get(this.departmentStorageKey);
    if (!departments || departments.length === 0) {
      refreshDepartmentsInStorage = true;
    }

    if (refreshDepartmentsInStorage) {
      const searchEvent: DepartmentSearchEvent = {
        paging: new PageEvent(),
      };
      searchEvent.paging.pageIndex = 0;
      searchEvent.paging.pageSize = 10000;
      departments = await (await firstValueFrom(this.getDepartments(searchEvent))).items;
      const minimalDepartments = departments.map(({ id, name }) => ({ id, name }));
      this.localStorageService.set(this.departmentStorageKey, minimalDepartments);
      this.localStorageService.set(this.departmentStorageTimestampKey, new Date().toISOString());
    }
    return departments;
  }
}
